import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router";

/**
 * A rolagem de cada tela, lembrada — Fase 6A.
 *
 * **O que ela conserta.** Rolar até o meio da lista de erros, abrir uma questão
 * e voltar devolvia a pessoa ao topo, com a linha que ela estava lendo em algum
 * lugar abaixo da dobra. O navegador faz isso sozinho em páginas de verdade; num
 * app de rota trocada em JavaScript, ninguém faz — a árvore é substituída e a
 * posição some junto.
 *
 * **Ao voltar, restaura; ao navegar, vai para o topo.** É a diferença entre
 * `POP` e `PUSH` do histórico, e é o que o navegador faria. Restaurar sempre
 * deixaria a tela nova aberta no meio; nunca restaurar é o defeito de cima.
 *
 * Em memória e por `location.key`: a posição é da **visita**, não do endereço.
 * Duas passagens pela mesma lista são duas leituras diferentes, e guardar por
 * endereço faria a segunda herdar a rolagem da primeira. Um `Map` que morre com
 * a aba basta — ele não é estado de aplicação (invariante 8 fala do processo do
 * servidor).
 *
 * A restauração espera **dois quadros**: o primeiro pinta o esqueleto, e rolar
 * ali miraria uma altura que ainda não existe. Não é perfeito para lista que
 * cresce depois do dado chegar, e é de propósito: adivinhar a altura final
 * exigiria medir cada tela, e errar por baixo é melhor do que pular.
 */
const POSICOES = new Map<string, number>();

export function RolagemLembrada() {
  const local = useLocation();
  const tipo = useNavigationType();

  useEffect(() => {
    const chave = local.key;

    if (tipo === "POP") {
      const y = POSICOES.get(chave);
      if (y !== undefined) {
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
      }
    } else {
      window.scrollTo(0, 0);
    }

    const guardar = () => POSICOES.set(chave, window.scrollY);
    window.addEventListener("scroll", guardar, { passive: true });
    return () => {
      guardar();
      window.removeEventListener("scroll", guardar);
    };
  }, [local.key, tipo]);

  return null;
}
